import { useEffect, useState, useCallback } from "react";

/**
 * useTheme — light/dark theme toggle persisted to localStorage.
 * Sets `data-theme` on <html>. CSS in index.css reacts via `html[data-theme="light"]`.
 */
export function useTheme() {
  const read = () => {
    try {
      return localStorage.getItem("ye_theme") || "dark";
    } catch { return "dark"; }
  };
  const [theme, setTheme] = useState(read);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem("ye_theme", theme); } catch (err) { console.warn("localStorage unavailable:", err); }
  }, [theme]);

  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  return { theme, setTheme, toggle };
}

/**
 * useDataVersion — global counter that bumps whenever the user clicks the
 * "Refresh data" button. Every module list re-fetches when this changes.
 * Uses a simple custom event to broadcast the bump across components.
 */
const REFRESH_EVENT = "ye:data-refresh";

export function triggerGlobalRefresh() {
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
}

export function useDataVersion() {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const on = () => setVersion((v) => v + 1);
    window.addEventListener(REFRESH_EVENT, on);
    return () => window.removeEventListener(REFRESH_EVENT, on);
  }, []);
  return version;
}
